import type { RumInitConfiguration } from '@datadog/browser-rum';
import { maskActionNameIds } from './actionNames';
import type { DatadogConfig } from '@ringba/config';
import {
  capConsoleErrorMessage,
  composeFilters,
  dropByScriptUrl,
  dropConsoleErrors,
  dropNoiseErrors,
  redactErrorStrings,
  redactEventUrlParams,
  scrubEventContext,
  suppressErrorEvents,
} from '@ringba/core/telemetry';
import { dropExtensionErrors } from './extensionErrors';
import { nameViewsByPage } from './pageKey';
import { dropInterventionReports } from './reports';
import type { InitRumOptions } from './types';

/** RUM discards an event over 256 KiB, and a console-sourced error carries every argument serialised into its message. */
const CONSOLE_ERROR_MAX_CHARS = 4096;

/**
 * Composed RUM `beforeSend` pipeline.
 *
 * Order:
 * - The drops run first: suppression, noise patterns, denied script URLs, extension errors, intervention reports, then the portal's own filters.
 * - A dropped event costs no redaction work.
 * - Then every event's view is named by its page, and an action's name loses its ids.
 * - The sanitisers run last, so a filter that reads a message or stack sees it as the browser produced it.
 */
export function createBeforeSend(
  config: Pick<DatadogConfig, 'suppressErrors'>,
  options: InitRumOptions,
): RumInitConfiguration['beforeSend'] {
  return composeFilters([
    suppressErrorEvents(config.suppressErrors),
    dropConsoleErrors(options.dropConsoleErrors),
    dropNoiseErrors(options.noisePatterns ?? []),
    dropByScriptUrl(options.denyScriptUrlPatterns ?? []),
    dropExtensionErrors(),
    dropInterventionReports(),
    ...(options.beforeSend ?? []),
    nameViewsByPage(options.basePath),
    maskActionNameIds(),
    redactEventUrlParams(),
    redactErrorStrings(),
    scrubEventContext(),
    capConsoleErrorMessage(CONSOLE_ERROR_MAX_CHARS),
  ]);
}
